/*
 * Modal de registro de jornada: muestra StartTimeRecordForm, EndTimeRecordForm,
 * CompleteWorklogObservationForm o el selector según el estado del worklog.
 */
import React, { useState } from 'react'
import { Modal, Spin, Button, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import StartTimeRecordForm from '../forms/startTimeRecordForm'
import EndTimeRecordForm from '../forms/endTimeRecordForm'
import { getPayload } from '../../../utils/auth'
import CreateTimeRecordForm from '../forms/CreateTimeRecordForm'
import CompleteWorklogObservationForm from '../forms/CompleteWorklogObservationForm'
import { useActiveWorklog } from '../hooks/useActiveWorklog'

interface TimeRecordModalProps {
  initialOpen?: boolean
  onClose?: () => void
}

type ModalView = 'selector' | 'start' | 'create'

const ActionSelector: React.FC<{ onStart: () => void; onCreate: () => void }> = ({ onStart, onCreate }) => {
  const { t } = useTranslation()
  return (
    <Space direction='vertical' style={{ width: '100%' }} size='middle'>
      <Button type='primary' block shape='round' onClick={onStart}>
        {t('new_activity')}
      </Button>
      <Button block shape='round' onClick={onCreate}>
        {t('finished_activity')}
      </Button>
    </Space>
  )
}

const TimeRecordModal: React.FC<TimeRecordModalProps> = ({
  initialOpen = true,
  onClose
}) => {
  const PAYLOAD = getPayload()
  const { t } = useTranslation()
  const [open, setOpen] = useState(initialOpen)
  const [view, setView] = useState<ModalView>('selector')

  const { activeWorklog, pendingObservationWorklog, loading: loadingById } = useActiveWorklog(PAYLOAD?.id)

  const handleCancel = () => {
    setOpen(false)
    onClose?.()
  }

  const renderContent = () => {
    if (loadingById) return <Spin />

    if (activeWorklog != null) {
      return (
        <EndTimeRecordForm
          worklogId={activeWorklog.id}
          startTime={activeWorklog.start_time}
          onCancel={handleCancel}
          onClose={handleCancel}
        />
      )
    }

    if (pendingObservationWorklog != null) {
      return (
        <CompleteWorklogObservationForm
          worklogId={pendingObservationWorklog.id}
          rowVersion={pendingObservationWorklog.row_version}
          startTime={pendingObservationWorklog.start_time}
          endTime={pendingObservationWorklog.end_time!}
          onCancel={handleCancel}
          onClose={handleCancel}
        />
      )
    }

    if (view === 'start') {
      return <StartTimeRecordForm
        onCancel={handleCancel}
        onClose={handleCancel}
        onBack={() => setView('selector')}
      />
    }

    if (view === 'create') {
      return (
        <CreateTimeRecordForm
          onCancel={handleCancel}
          onClose={handleCancel}
          onBack={() => setView('selector')}
        />
      )
    }

    return <ActionSelector onStart={() => setView('start')} onCreate={() => setView('create')} />
  }

  return (
    <Modal
      open={open}
      title={t('time_record')}
      onCancel={handleCancel}
      onOk={handleCancel}
      footer={null}
      destroyOnClose
    >
      {renderContent()}
    </Modal>
  )
}

export default TimeRecordModal
